import { PaymentProcessor } from "@/types/auth";
import { PublicInvoice } from "@/types/billing";
import {
  ChargeSavedMethodResponse,
  PaymentMethodPatient,
} from "@/types/payment";
import { PaymentLinkDetails, VerifyPatient } from "@/types/payment-link";
import { BookingFeePayinResponse } from "@/types/rainforest";
import { BookingFeeCollectionMethod } from "@/types/tenant";

import { requestData } from "@/api/api-config";

function buildPaymentLinkParams(
  isSurcharged?: boolean,
  savePaymentMethod?: boolean,
  invoiceId?: string,
): string {
  const params = new URLSearchParams();
  if (invoiceId) {
    params.set("invoice_id", invoiceId);
  }
  if (isSurcharged !== undefined) {
    params.set("isSurcharged", String(isSurcharged));
  }
  if (savePaymentMethod !== undefined) {
    params.set("save_payment_method", String(savePaymentMethod));
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

// `invoiceId` limits the link to the charges that invoice covers; without it
// the link collects the patient's whole outstanding balance.
export async function fetchPaymentLink(
  token: string,
  isSurcharged?: boolean,
  savePaymentMethod?: boolean,
  invoiceId?: string,
): Promise<PublicInvoice[] | null> {
  return await requestData<PublicInvoice[]>(
    `billing/payment-link/${token}${buildPaymentLinkParams(isSurcharged, savePaymentMethod, invoiceId)}`,
  );
}

export async function verifyPatient(
  verifyPatient: VerifyPatient,
): Promise<PaymentLinkDetails | null> {
  return await requestData<PaymentLinkDetails>(
    "billing/payment-link/verify-patient",
    "post",
    {
      json: verifyPatient,
    },
  );
}

export async function createPayinConfigBookingFee(
  eventId: string,
  patientId: string,
  isSurcharged: boolean,
): Promise<BookingFeePayinResponse | null> {
  // catchError=false so 4xx (missing event, wrong patient) throws and
  // useMutation's onError fires — otherwise the event-pay page would stall
  // on the loading skeleton with no terminal fallback.
  return await requestData<BookingFeePayinResponse>(
    `billing/create-payin-config-booking-fee/${eventId}/${patientId}?isSurcharged=${isSurcharged}`,
    "post",
    undefined,
    false,
  );
}

export async function chargeBookingFeeWithSavedMethod(
  eventId: string,
  paymentMethodId: string,
): Promise<ChargeSavedMethodResponse | null> {
  return await requestData<ChargeSavedMethodResponse>(
    `patient/billing/booking-fee/${eventId}/charge-saved-method`,
    "post",
    { json: { paymentMethodId } },
    false,
  );
}

/** What happened to a booking fee staff collected right after booking. */
export interface BookingFeeCollectResult {
  method: BookingFeeCollectionMethod;
  chargeId: string;
  amountCents: number;
  /** False when the method couldn't run; the fee is then on the patient's balance. */
  collected: boolean;
  cardBrand?: string | null;
  cardLast4?: string | null;
  phoneNumber?: string | null;
  failureReason?: string | null;
  /** The card may or may not have been charged; staff should check payments first. */
  outcomeUnknown?: boolean;
  /** Set on a declined card; word it with `shortReasonForDeclineClass` or `messageForDeclineClass`. */
  declineClass?: string | null;
}

export async function collectBookingFee(
  eventId: string,
  patientId: string,
  method: BookingFeeCollectionMethod,
  creatorId: string,
): Promise<BookingFeeCollectResult | null> {
  // catchError=false: the appointment is already booked by now, so the form
  // has to tell staff the fee wasn't set up rather than fail silently.
  return await requestData<BookingFeeCollectResult>(
    `billing/booking-fee/${eventId}/${patientId}/collect`,
    "post",
    { json: { method, creatorId } },
    false,
  );
}

export async function regeneratePayinConfig(
  patientId: string,
  isSurcharged?: boolean,
  savePaymentMethod?: boolean,
  invoiceId?: string,
): Promise<PublicInvoice[] | null> {
  return await requestData<PublicInvoice[]>(
    `billing/payment-link/${patientId}${buildPaymentLinkParams(isSurcharged, savePaymentMethod, invoiceId)}`,
  );
}

export interface EmbedChargeItem {
  name: string;
  quantity: number;
  price: number;
  discountAmount: number;
  lineTotal: number;
}

export interface EmbedChargeSummary {
  totalOutstanding: number;
  items: EmbedChargeItem[];
  totalDiscount: number;
}

export interface EmbedPaymentConfig {
  /** Which processor this session belongs to; the other half's fields are null. */
  processor?: PaymentProcessor;
  sessionKey: string;
  payinConfigId: string;
  sandbox: boolean;
  /** Stripe only. */
  publishableKey?: string | null;
  stripeAccount?: string | null;
  clientSecret?: string | null;
  patient: {
    id: string;
    firstName: string;
    lastName: string | null;
  };
  fee: number;
  amountCents: number;
  charge?: EmbedChargeSummary | null;
}

export interface EmbedStoreDetailsConfig {
  /** Which processor answered; says which half of the fields below is populated. */
  processor?: PaymentProcessor;
  sessionKey: string;
  paymentMethodConfigId: string;
  sandbox: boolean;
  /** Stripe only — the SetupIntent's secret and the account that owns it. */
  publishableKey?: string | null;
  stripeAccount?: string | null;
  clientSecret?: string | null;
  patient: {
    id: string;
    firstName: string;
    lastName: string | null;
  };
}

export async function createEmbedStoreDetailsConfig(
  patientId: string,
): Promise<EmbedStoreDetailsConfig | null> {
  return await requestData<EmbedStoreDetailsConfig>(
    `billing/embed-store-details/${patientId}`,
    "post",
  );
}

/** Persist Rainforest-approved method and return Decoda payment method id (public embed). */
export async function registerEmbedStoreDetailsPaymentMethod(
  patientId: string,
  rainforestPaymentMethodId: string,
  paymentMethodConfigId: string,
): Promise<PaymentMethodPatient | null> {
  return await requestData<PaymentMethodPatient>(
    `billing/embed-store-details/${patientId}/payment-method`,
    "post",
    {
      json: { rainforestPaymentMethodId, paymentMethodConfigId },
    },
  );
}

export async function createEmbedPaymentConfig(
  patientId: string,
  options: {
    amountCents?: number;
    chargeId?: string;
    isSurcharged?: boolean;
    /**
     * The embed's own method choice, straight from its `allowedMethods` URL
     * parameter. Sent to the server because Stripe decides what a payment
     * offers when it is created, not in the browser.
     */
    allowedMethods?: string | null;
  },
): Promise<EmbedPaymentConfig | null> {
  const {
    amountCents,
    chargeId,
    isSurcharged = true,
    allowedMethods,
  } = options;
  const params = new URLSearchParams();
  params.set("is_surcharged", String(isSurcharged));
  if (allowedMethods) params.set("allowed_methods", allowedMethods);
  if (chargeId) {
    params.set("charge_id", chargeId);
  } else if (amountCents != null && amountCents > 0) {
    params.set("amount_cents", String(amountCents));
  }
  return await requestData<EmbedPaymentConfig>(
    `billing/embed-payment/${patientId}?${params.toString()}`,
    "post",
  );
}
